import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { isModalOpen, setToastMessage } from '../redux/FlowReducer';
import { Box, Typography, TextField, Card, CardContent, Tabs, Tab, IconButton, Chip } from '@mui/material';
import { Trash2, ChevronRight, ChevronDown } from 'lucide-react';
import VariableAutocomplete from '../VariableAutocomplete';
import PanelShell from './PanelShell';

const ServicePanel = ({ nodeType, nodeId, data, onChange }) => {
  const dispatch = useDispatch();
  const vars = useSelector((state) => state.flow.vars) || [];
  const tags = useSelector((state) => state.flow.tags) || [];
  const localVars = useSelector((state) => state.flow.localVars) || [];

  // Basic service configuration
  const [serviceName, setServiceName] = useState(data?.serviceName || '');
  const [methodName, setMethodName] = useState(data?.methodName || '');

  // Parameter management
  const [inputParameters, setInputParameters] = useState([]);
  const [outputParameters, setOutputParameters] = useState([]);
  const [focusedInput, setFocusedInput] = useState(null);
  const [parameterMappings, setParameterMappings] = useState({});

  // UI state
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedObjects, setExpandedObjects] = useState({});

  // Parse variable string to get proper variable object
  const parseVariableString = (value, inputType) => {
    if (!value) return null;

    if (inputType === 'constant' || inputType === 'text') {
      return {
        variableId: value,
        variableName: value,
        variableType: 'constant'
      };
    }

    if (value.startsWith('lv_')) {
      const varName = value.substring(3);
      const foundLocalVar = localVars.find(v => v.name === varName);
      return {
        variableId: value,
        variableName: foundLocalVar ? foundLocalVar.name : value,
        variableType: 'LV'
      };
    }

    if (value.startsWith('var_')) {
      const varId = value.substring(4);
      const foundVar = vars.find(v => {
        const varIdValue = v.varId !== undefined ? v.varId : v.varID;
        return String(varIdValue) === String(varId);
      });
      return {
        variableId: value,
        variableName: foundVar ? foundVar.varName : value,
        variableType: 'V'
      };
    }

    if (value.startsWith('tag_')) {
      const tagId = value.substring(4);
      const foundTag = tags.find(t => {
        const tagIdValue = t.tagId !== undefined ? t.tagId : t.tagID;
        return String(tagIdValue) === String(tagId);
      });
      return {
        variableId: value,
        variableName: foundTag ? foundTag.tagName : value,
        variableType: 'T'
      };
    }

    return {
      variableId: value,
      variableName: value,
      variableType: 'constant'
    };
  };

  // Parse parameters from JSON string
  const parseParametersFromJSON = (jsonString) => {
    if (!jsonString) return [];
    try {
      const parsed = JSON.parse(jsonString);
      if (Array.isArray(parsed)) {
        return parsed.map((param, idx) => ({
          id: `param_${idx}_${Date.now()}`,
          name: param.name || `param${idx}`,
          type: param.type || 'string',
          description: param.description || '',
          required: param.required || false,
          value: param.value || '',
          ...param
        }));
      } else if (typeof parsed === 'object') {
        return Object.keys(parsed).map((key, idx) => ({
          id: `param_${idx}_${Date.now()}`,
          name: key,
          type: typeof parsed[key],
          value: parsed[key],
          required: false
        }));
      }
    } catch (e) {
      console.error('Error parsing parameters:', e);
    }
    return [];
  };

  // Initialize data when component mounts or data changes
  useEffect(() => {
    if (!data) return;

    setServiceName(data.serviceName || '');
    setMethodName(data.methodName || '');

    // Load input parameters
    if (data.inputParameters && Array.isArray(data.inputParameters)) {
      setInputParameters(data.inputParameters);
    } else if (data.parameters) {
      // Fallback: try to parse from parameters field
      const params = parseParametersFromJSON(
        typeof data.parameters === 'string' ? data.parameters : JSON.stringify(data.parameters)
      );
      setInputParameters(params);
    }

    // Load output parameters
    if (data.outputParameters && Array.isArray(data.outputParameters)) {
      setOutputParameters(data.outputParameters);
    }

    // Load parameter mappings
    if (data.parameterMappings) {
      setParameterMappings(data.parameterMappings);
    } else {
      // Try to rebuild mappings from legacy data
      const newMappings = {};
      if (data.inputParameters && Array.isArray(data.inputParameters)) {
        data.inputParameters.forEach(param => {
          if (param.variableId || param.value) {
            const mapping = parseVariableString(
              param.variableId || param.value,
              param.inputType || (param.variableType === 'constant' ? 'constant' : 'variable')
            );
            if (mapping) {
              newMappings[param.id] = mapping;
            }
          }
        });
      }
      setParameterMappings(newMappings);
    }
  }, [data, vars, tags, localVars]);

  const getColorForType = (type) => {
    switch(type) {
      case 'V': return '#fff3e0';
      case 'T': return '#edbc45';
      case 'LV': return '#f3e5f5';
      case 'constant': return '#e0e0e0';
      default: return '#f5f5f5';
    }
  };

  const handleVariableSelect = (event, variable) => {
    if (!focusedInput || !variable) return;

    let variableId;
    let variableName;
    const variableType = variable.type;

    switch (variableType) {
      case 'LV':
        variableId = `lv_${variable.name}`;
        variableName = variable.name;
        break;
      case 'V':
        const varIdValue = variable.varID || variable.varId || variable.id;
        variableId = `var_${varIdValue}`;
        variableName = variable.varName || variable.name;
        break;
      case 'T':
        const tagIdValue = variable.tagID || variable.tagId || variable.id;
        variableId = `tag_${tagIdValue}`;
        variableName = variable.tagName || variable.name;
        break;
      case 'constant':
        variableId = variable.name;
        variableName = variable.name;
        break;
      default:
        variableId = variable.id;
        variableName = variable.name;
    }

    setParameterMappings(prev => ({
      ...prev,
      [focusedInput]: {
        variableId,
        variableName,
        variableType,
        paramId: focusedInput
      }
    }));
  };

  const removeMappingForParameter = (paramId) => {
    setParameterMappings(prev => {
      const updated = { ...prev };
      delete updated[paramId];
      return updated;
    });
  };

  const handleAddParameter = () => {
    const newParam = {
      id: `param_${Date.now()}`,
      name: `param${inputParameters.length + 1}`,
      type: 'string',
      description: '',
      required: false
    };
    setInputParameters(prev => [...prev, newParam]);
  };

  const handleRemoveParameter = (paramId) => {
    setInputParameters(prev => prev.filter(p => p.id !== paramId));
    removeMappingForParameter(paramId);
  };

  const handleParameterNameChange = (paramId, newName) => {
    setInputParameters(prev => prev.map(p =>
      p.id === paramId ? { ...p, name: newName } : p
    ));
  };

  const handleParameterTypeChange = (paramId, newType) => {
    setInputParameters(prev => prev.map(p =>
      p.id === paramId ? { ...p, type: newType } : p
    ));
  };

  const handleParameterRequiredChange = (paramId, required) => {
    setInputParameters(prev => prev.map(p =>
      p.id === paramId ? { ...p, required } : p
    ));
  };

  const InputCard = ({ param }) => {
    const isFocused = focusedInput === param.id;
    const mapping = parameterMappings[param.id];
    const [isEditing, setIsEditing] = useState(false);

    return (
      <Card
        onClick={() => setFocusedInput(param.id)}
        sx={{
          cursor: 'pointer',
          border: isFocused ? '2px solid #6a1b9a' : '1px solid #ddd',
          backgroundColor: isFocused ? '#f3e5f5' : 'white',
          mb: 1
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {isEditing ? (
              <TextField
                size="small"
                value={param.name}
                onChange={(e) => handleParameterNameChange(param.id, e.target.value)}
                onBlur={() => setIsEditing(false)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                sx={{ flex: 1 }}
              />
            ) : (
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ flex: 1 }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
              >
                {param.name}
                {param.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
            )}

            <Chip
              label={param.type || 'string'}
              size="small"
              sx={{ fontSize: '0.7rem' }}
            />

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveParameter(param.id);
              }}
              sx={{ ml: 'auto' }}
            >
              <Trash2 size={14} />
            </IconButton>
          </Box>

          {param.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {param.description}
            </Typography>
          )}

          {mapping?.variableName ? (
            <Box sx={{
              p: 1,
              backgroundColor: getColorForType(mapping.variableType),
              borderRadius: 1,
              fontSize: '0.9rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{mapping.variableName}</span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeMappingForParameter(param.id);
                }}
              >
                <Trash2 size={12} />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              Değişken seçilmedi
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const OutputCard = ({ param }) => {
    return (
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
              {param.name}
            </Typography>
            <Chip
              label={param.type || 'string'}
              size="small"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
          {param.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {param.description}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleSave = () => {
    if (!serviceName.trim()) {
      dispatch(setToastMessage({
        type: 'error',
        message: 'Lütfen servis adını girin.'
      }));
      return;
    }

    // Check required parameters
    const requiredParams = inputParameters.filter(p => p.required);
    const missingMappings = requiredParams.filter(p => !parameterMappings[p.id]);

    if (missingMappings.length > 0) {
      dispatch(setToastMessage({
        type: 'error',
        message: `Gerekli parametreler eksik: ${missingMappings.map(p => p.name).join(', ')}`
      }));
      return;
    }

    // Build parameters object with mappings
    const parametersWithMappings = inputParameters.map(param => ({
      ...param,
      variableId: parameterMappings[param.id]?.variableId,
      variableName: parameterMappings[param.id]?.variableName,
      variableType: parameterMappings[param.id]?.variableType,
      inputType: parameterMappings[param.id]?.variableType === 'constant' ? 'constant' : 'variable'
    }));

    // Create title
    const title = methodName
      ? `${serviceName}.${methodName}()`
      : `${serviceName}()`;

    const nodeData = {
      serviceName,
      methodName,
      inputParameters: parametersWithMappings,
      outputParameters,
      parameterMappings,
      parameters: parametersWithMappings.reduce((acc, p) => {
        acc[p.name] = parameterMappings[p.id]?.variableId || '';
        return acc;
      }, {}),
      label: 'Service',
      Title: title
    };

    console.log('Saving Service Node Data:', nodeData);

    onChange(nodeData);
    dispatch(isModalOpen());
    dispatch(setToastMessage({
      type: 'success',
      message: 'Servis konfigürasyonu kaydedildi.'
    }));
  };

  if (nodeType !== "Service") return null;

  return (
    <PanelShell title="Servis Çağrısı" onSave={handleSave}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        {/* Service Configuration */}
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Servis Adı
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={serviceName}
            onChange={(e) => setServiceName(e.target.value)}
            placeholder="ör: MyService"
          />
        </Box>

        <Box>
          <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
            Metod Adı (Opsiyonel)
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={methodName}
            onChange={(e) => setMethodName(e.target.value)}
            placeholder="ör: GetData"
          />
        </Box>

        {/* Tabs for Input/Output Parameters */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
            <Tab label="Giriş Parametreleri" />
            <Tab label="Çıkış Parametreleri" />
          </Tabs>
        </Box>

        {/* Main Content Area */}
        <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
          {/* Left Panel - Parameters */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedTab === 0 ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Parametreler ({inputParameters.length})
                  </Typography>
                  <Box
                    onClick={handleAddParameter}
                    sx={{
                      cursor: 'pointer',
                      color: '#6a1b9a',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    + Parametre Ekle
                  </Box>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {inputParameters.length > 0 ? (
                    inputParameters.map((param) => (
                      <InputCard key={param.id} param={param} />
                    ))
                  ) : (
                    <Box sx={{
                      p: 3,
                      textAlign: 'center',
                      color: 'text.disabled',
                      border: '1px dashed #ddd',
                      borderRadius: 1
                    }}>
                      <Typography variant="body2">
                        Henüz parametre eklenmedi
                      </Typography>
                      <Typography variant="caption">
                        Parametre eklemek için yukarıdaki butona tıklayın
                      </Typography>
                    </Box>
                  )}
                </Box>

                {focusedInput && (
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                    Sağdaki listeden bir değişken seçin
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2" fontWeight={600} color="text.secondary" mb={1}>
                  Çıkış Parametreleri ({outputParameters.length})
                </Typography>

                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {outputParameters.length > 0 ? (
                    outputParameters.map((param) => (
                      <OutputCard key={param.id} param={param} />
                    ))
                  ) : (
                    <Box sx={{
                      p: 3,
                      textAlign: 'center',
                      color: 'text.disabled',
                      border: '1px dashed #ddd',
                      borderRadius: 1
                    }}>
                      <Typography variant="body2">
                        Çıkış parametresi tanımlanmadı
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>

          {/* Right Panel - Variable Selector (only for input tab) */}
          {selectedTab === 0 && (
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600} mb={1} color="text.secondary">
                Değişken / Sabit Seçimi
              </Typography>
              <VariableAutocomplete
                value={null}
                onChange={handleVariableSelect}
                placeholder="Tag, değişken veya sabit ara..."
                showConstants={true}
              />

              {focusedInput && parameterMappings[focusedInput] && (
                <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Seçili:
                  </Typography>
                  <Box sx={{
                    mt: 0.5,
                    p: 1,
                    backgroundColor: getColorForType(parameterMappings[focusedInput].variableType),
                    borderRadius: 1
                  }}>
                    {parameterMappings[focusedInput].variableName}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Summary */}
        <Box sx={{
          pt: 1,
          borderTop: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="caption" color="text.secondary">
            {Object.keys(parameterMappings).length} parametre eşleştirildi
          </Typography>
        </Box>
      </Box>
    </PanelShell>
  );
};

export default ServicePanel;
